import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Leaf, 
  ArrowLeft, 
  ArrowRight,
  Mail, 
  Lock, 
  User, 
  Building2,
  GraduationCap,
  Briefcase,
  Heart,
  Users,
  CheckCircle
} from "lucide-react";

export default function Register() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: choose mode, 2: form
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    organization: "",
    role: "user",
    account_type: "", // "personal" or "business"
    user_category: "" // "student", "individual", "ngo", "company"
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const personalCategories = [
    { id: "student", label: t('register.categories.student'), icon: GraduationCap, desc: t('register.categories.studentDesc') },
    { id: "individual", label: t('register.categories.individual'), icon: User, desc: t('register.categories.individualDesc') },
  ];

  const businessCategories = [
    { id: "company", label: t('register.categories.company'), icon: Briefcase, desc: t('register.categories.companyDesc') },
    { id: "ngo", label: t('register.categories.ngo'), icon: Heart, desc: t('register.categories.ngoDesc') },
    { id: "institution", label: t('register.categories.institution'), icon: Building2, desc: t('register.categories.institutionDesc') },
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectMode = (mode, category) => {
    setFormData(prev => ({ 
      ...prev, 
      account_type: mode,
      user_category: category
    }));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      toast.success("Compte créé avec succès !");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Choose mode
  if (step === 1) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card/50">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Leaf className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('register.back')}
              </Button>
            </Link>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              {t('register.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('register.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Personal Mode */}
            <Card className="border-2 border-transparent hover:border-primary/50 transition-all cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-4">
                  <User className="w-7 h-7 text-blue-500" />
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                  {t('register.personal.title')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('register.personal.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-600 font-medium">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t('register.personal.price')}</span>
                </div>
                <div className="space-y-3 pt-2">
                  {personalCategories.map((cat) => (
                    <Button
                      key={cat.id}
                      variant="outline"
                      className="w-full justify-start h-auto py-3 px-4 hover:bg-primary/5 hover:border-primary"
                      onClick={() => selectMode("personal", cat.id)}
                      data-testid={`select-${cat.id}`}
                    >
                      <cat.icon className="w-5 h-5 mr-3 text-primary" />
                      <div className="text-left">
                        <div className="font-medium">{cat.label}</div>
                        <div className="text-xs text-muted-foreground">{cat.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Business Mode */}
            <Card className="border-2 border-transparent hover:border-primary/50 transition-all cursor-pointer group">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4">
                  <Briefcase className="w-7 h-7 text-emerald-500" />
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                  {t('register.business.title')}
                </CardTitle>
                <CardDescription className="text-base">
                  {t('register.business.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-muted-foreground font-medium">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t('register.business.price')}</span>
                </div>
                <div className="space-y-3 pt-2">
                  {businessCategories.map((cat) => (
                    <Button
                      key={cat.id}
                      variant="outline"
                      className="w-full justify-start h-auto py-3 px-4 hover:bg-primary/5 hover:border-primary"
                      onClick={() => selectMode("business", cat.id)}
                      data-testid={`select-${cat.id}`}
                    >
                      <cat.icon className="w-5 h-5 mr-3 text-primary" />
                      <div className="text-left">
                        <div className="font-medium">{cat.label}</div>
                        <div className="text-xs text-muted-foreground">{cat.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-muted-foreground">
              {t('register.form.hasAccount')}{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('register.form.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration form
  const isPersonal = formData.account_type === "personal";

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className={`absolute inset-0 ${isPersonal ? 'bg-gradient-to-br from-blue-600/80 to-cyan-500/60' : 'bg-gradient-to-br from-primary/80 to-accent/60'}`} />
        <img
          src={isPersonal 
            ? "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85"
            : "https://images.unsplash.com/photo-1761971974926-a86159bc3471?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzdXN0YWluYWJsZSUyMGNvcnBvcmF0ZSUyMG9mZmljZSUyMG1vZGVybiUyMG5hdHVyZXxlbnwwfHx8fDE3NzIyODExNTl8MA&ixlib=rb-4.1.0&q=85"
          }
          alt={isPersonal ? "Students working" : "Sustainable office"}
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white space-y-6">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isPersonal ? 'bg-blue-500/30' : 'bg-primary/30'}`}>
              {isPersonal ? <User className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
              {isPersonal ? t('register.personal.badge') : t('register.business.badge')}
            </div>
            <h2 className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>
              {t('register.hero.personalTitle')}
            </h2>
            <p className="text-lg text-white/80 max-w-md">
              {isPersonal
                ? t('register.hero.personalSubtitle')
                : t('register.hero.businessSubtitle')
              }
            </p>
            {isPersonal && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/30 text-sm">
                <CheckCircle className="w-4 h-4" />
                {t('register.personal.price')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <button 
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('register.back')}
          </button>
          
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</h1>
              <p className="text-sm text-muted-foreground">
                {isPersonal ? t('register.personal.badge') : t('register.business.badge')}
              </p>
            </div>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>{t('register.form.title')}</CardTitle>
              <CardDescription>
                {isPersonal 
                  ? t('register.personal.description')
                  : t('register.business.description')
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">{t('register.form.name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="full_name"
                      type="text"
                      placeholder={t('register.form.namePlaceholder')}
                      value={formData.full_name}
                      onChange={(e) => handleChange("full_name", e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t('register.form.email')}
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('register.form.emailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="pl-10"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                
                {!isPersonal && (
                  <div className="space-y-2">
                    <Label htmlFor="organization">{t('register.form.organization')}</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="organization"
                        type="text"
                        placeholder={t('register.form.organizationPlaceholder')}
                        value={formData.organization}
                        onChange={(e) => handleChange("organization", e.target.value)}
                        className="pl-10"
                        required
                        data-testid="register-org-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">{t('register.form.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder={t('register.form.passwordPlaceholder')}
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full rounded-full" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? t('pricing.loading') : t('register.form.submit')}
                </Button>
              </form>
              
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('register.form.orContinueWith')}</span>
                </div>
              </div>
              
              {/* Google Auth Button */}
              <a 
                href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
                className="w-full"
              >
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full rounded-full"
                  data-testid="google-register-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('register.form.google')}
                </Button>
              </a>
              
              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">{t('register.form.hasAccount')} </span>
                <Link to="/login" className="text-primary hover:underline font-medium">
                  {t('register.form.signIn')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
